import {
	type AggregationFn,
	type ColumnDef,
	type ExpandedState,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type RowSelectionState,
	type SortingFn,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";

import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import type { Entry } from "@/shared/types/entries";
import { classifyEntry, type FilterState } from "@/v2/components/FilterPopover";
import type { GroupMode, SortMode } from "@/v2/components/SortPopover";
import { getEntryHourGroupLabel, groupingForMode } from "../utils/grouping";
import { getEntryEndTimeSortValue } from "../utils/time";

export interface UseEntriesTableOptions {
	entries: Entry[];
	filter: FilterState;
	sort: SortMode;
	group: GroupMode;
	isMultiDate: boolean;
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
	rowSelection: RowSelectionState;
	onRowSelectionChange: OnChangeFn<RowSelectionState>;
}

const stringSort: SortingFn<Entry> = (left, right, columnId) => {
	return String(left.getValue(columnId) ?? "").localeCompare(
		String(right.getValue(columnId) ?? ""),
	);
};

const firstValueAggregation: AggregationFn<Entry> = (columnId, leafRows) => {
	return leafRows[0]?.getValue(columnId);
};

function createEndTimeAggregation(sort: SortMode): AggregationFn<Entry> {
	return (columnId, leafRows) => {
		const values = leafRows
			.map((row) => row.getValue<number | undefined>(columnId))
			.filter((value): value is number => typeof value === "number");
		if (values.length === 0) return undefined;
		return sort === "time-asc" ? Math.min(...values) : Math.max(...values);
	};
}

function sortingForMode(sort: SortMode): SortingState {
	switch (sort) {
		case "time-desc":
			return [{ id: "endTime", desc: true }];
		case "time-asc":
			return [{ id: "endTime", desc: false }];
		case "duration-desc":
			return [{ id: "duration", desc: true }];
		case "duration-asc":
			return [{ id: "duration", desc: false }];
		case "matter-asc":
			return [{ id: "matter", desc: false }];
		case "matter-desc":
			return [{ id: "matter", desc: true }];
	}
}

export function useEntriesTable({
	entries,
	filter,
	sort,
	group,
	isMultiDate,
	getRestrictedWords,
	rowSelection,
	onRowSelectionChange,
}: UseEntriesTableOptions) {
	const [expanded, setExpanded] = useState<ExpandedState>(true);
	const columns = useMemo<ColumnDef<Entry>[]>(
		() => [
			{
				id: "application",
				accessorFn: (entry) => entry.application || "Unknown",
				getGroupingValue: (entry) => entry.application || "Unknown",
				filterFn: (row, columnId, hiddenApps: Set<string>) => {
					const application = row.getValue<string>(columnId);
					return application === "Unknown" || !hiddenApps.has(application);
				},
				sortingFn: stringSort,
				aggregationFn: firstValueAggregation,
			},
			{
				id: "entryStatus",
				accessorFn: (entry) => classifyEntry(entry, getRestrictedWords),
				filterFn: (row, columnId, statuses: Set<string>) => {
					return statuses.has(row.getValue<string>(columnId));
				},
				enableGrouping: false,
			},
			{
				id: "endTime",
				accessorFn: getEntryEndTimeSortValue,
				sortingFn: "basic",
				aggregationFn: createEndTimeAggregation(sort),
				sortUndefined: "last",
			},
			{
				id: "duration",
				accessorFn: (entry) => entry.units,
				aggregationFn: "sum",
			},
			{
				id: "matter",
				accessorFn: (entry) => entry.matter?.description || "No matter",
				// Group by the stable matter id; distinct matters can share a description
				// and would otherwise merge into one group. Header shows the description.
				getGroupingValue: (entry) => entry.matter?.id ?? "No matter",
				sortingFn: stringSort,
				aggregationFn: firstValueAggregation,
			},
			{
				id: "groupHour",
				accessorFn: (entry) => getEntryHourGroupLabel(entry, isMultiDate),
				sortingFn: stringSort,
			},
		],
		[isMultiDate, getRestrictedWords, sort],
	);

	const columnFilters = useMemo(
		() => [
			{ id: "application", value: filter.hiddenApps },
			{ id: "entryStatus", value: filter.statuses },
		],
		[filter.hiddenApps, filter.statuses],
	);

	const sorting = useMemo(() => sortingForMode(sort), [sort]);
	const grouping = useMemo(() => groupingForMode(group), [group]);

	return useReactTable({
		data: entries,
		columns,
		state: {
			columnFilters,
			expanded,
			grouping,
			rowSelection,
			sorting,
		},
		onExpandedChange: setExpanded,
		onRowSelectionChange,
		enableRowSelection: (row) => !row.getIsGrouped(),
		autoResetExpanded: false,
		getRowId: (entry) => String(entry.id),
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
	});
}
